import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  /** Optional label shown above the error message — useful when
   *  the boundary is scoped (e.g., "Feedback card" vs the app root). */
  scope?: string;
  /** Optional renderer for a custom fallback. Default fallback shows
   *  the error message + a reload button, scoped to the boundary. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * App-level error boundary. Without one, a throw anywhere in the React
 * tree unmounts the whole app and you see a blank page with no clue
 * what happened — which is exactly the failure mode we hit on the star
 * rating click. Scope this around suspect surfaces (PostExportFeedback,
 * lazy route bodies, etc.) and wrap the App in a top-level instance as
 * a last-line catch.
 *
 * React only catches errors thrown during render, in lifecycle methods,
 * and in constructors. Async exceptions (Promise rejections in event
 * handlers, fetch failures) still bypass it — those need explicit
 * try/catch at the call site.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced to the browser console for the operator. Sentry / a
    // remote logger would plug in here in a future iteration.
    if (typeof console !== "undefined") {
      console.error(
        `[ErrorBoundary${this.props.scope ? " · " + this.props.scope : ""}]`,
        error,
        info.componentStack,
      );
    }
  }

  reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div className="card p-4 text-sm text-ink" role="alert">
        <p className="font-display text-base font-semibold">
          {this.props.scope
            ? `Something broke in: ${this.props.scope}`
            : "Something broke."}
        </p>
        <p className="mt-1 text-xs text-muted">
          {error.message || "Unknown error — check the browser console for details."}
        </p>
        <button
          type="button"
          onClick={this.reset}
          className="btn-glass mt-3 text-xs"
        >
          Try again
        </button>
      </div>
    );
  }
}
